import type { TranslationKey } from '@dreamgame/shared';
import { signal } from '@preact/signals';
import { tv } from 'tailwind-variants';
import {
  closeConstructionPanel,
  isConstructionPanelOpen,
  openConstructionPanel,
} from '../../state/construction';
import { t } from '../../state/i18n';
import {
  closeStockpilePanel,
  isStockpilePanelOpen,
  openStockpilePanel,
} from '../../state/stockpile';

// ============================================================================
// BottomToolbar - Main action bar for Build/Stockpile tabs
// ============================================================================

// Toolbar tab identifiers
export const ToolbarTab = {
  BUILD: 'build',
  STOCKPILE: 'stockpile',
} as const;

export type ToolbarTabValue = (typeof ToolbarTab)[keyof typeof ToolbarTab];

// Active tab signal (null = both closed)
export const activeToolbarTab = signal<ToolbarTabValue | null>(null);

// Styles using tailwind-variants - Fantasy Medieval Theme
const toolbar = tv({
  slots: {
    root: [
      'fixed bottom-[1.1em] left-1/2 -translate-x-1/2',
      'z-[999]',
      'flex items-center gap-[0.25em]',
      'animate-slide-up',
      'text-[clamp(0.95rem,0.9rem+0.35vw,1.15rem)]',
    ],

    // The toolbar background
    container: [
      'relative',
      'flex items-center gap-[0.6em]',
      'px-[0.85em] py-[0.55em]',
      'bg-gradient-to-b from-leather to-leather-dark',
      'border-2 border-gold-filigree/50',
      'rounded-xl',
      'shadow-tome',
      // Inner highlight
      'before:content-[""]',
      'before:absolute before:inset-[0.12em]',
      'before:rounded-[0.62em]',
      'before:border before:border-gold-filigree/20',
      'before:pointer-events-none',
    ],

    // Tab button
    tabButton: [
      'relative',
      'flex flex-col items-center justify-center gap-[0.15em]',
      'w-[4.4em] h-[3.9em]',
      'rounded-[0.55em]',
      'border border-transparent',
      'bg-leather-dark/50',
      'text-parchment',
      'cursor-pointer',
      'transition-all duration-200',
      'hover:bg-leather-light/30',
      'hover:border-gold-filigree/30',
      'active:scale-95',
      'group',
    ],

    tabButtonActive: [
      'bg-parchment/20',
      'border-gold-filigree',
      'text-gold-bright',
      'shadow-inner',
    ],

    tabIcon: [
      'w-[1.95em] h-[1.95em]',
      'flex items-center justify-center',
      'transition-transform duration-200',
      'group-hover:scale-110',
    ],

    tabLabel: ['text-[0.62em] font-fantasy tracking-wide font-bold uppercase', 'leading-none'],

    // Decorative gold studs
    stud: ['w-[0.5em] h-[0.5em]', 'rounded-full', 'bg-gold-filigree', 'shadow-sm', 'opacity-60'],
  },
});

// Tab configuration
const TABS: ReadonlyArray<{
  id: ToolbarTabValue;
  labelKey: TranslationKey;
  icon: string;
}> = [
  { id: ToolbarTab.BUILD, labelKey: 'ui.tabs.build', icon: '/ui/toolbar-build-icon.png' },
  {
    id: ToolbarTab.STOCKPILE,
    labelKey: 'ui.tabs.stockpile',
    icon: '/ui/toolbar-stockpile-icon.png',
  },
];

const TabIcon = ({ src, tabId }: { src: string; tabId: ToolbarTabValue }) => {
  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is not interactive
    <img
      src={src}
      alt=""
      class="w-[1.6em] h-[1.6em] object-contain drop-shadow-md"
      onError={(e) => {
        // Fallback to emoji if image fails to load
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        const parent = target.parentElement;
        if (parent) {
          parent.textContent = tabId === ToolbarTab.BUILD ? '🔨' : '📦';
        }
      }}
    />
  );
};

export const BottomToolbar = () => {
  const styles = toolbar();

  const handleTabClick = ({ tab }: { tab: ToolbarTabValue }) => {
    const currentTab = activeToolbarTab.value;

    // Toggle off if clicking active tab
    if (currentTab === tab) {
      activeToolbarTab.value = null;
      closeConstructionPanel();
      closeStockpilePanel();
      return;
    }

    // Switch to new tab
    activeToolbarTab.value = tab;

    if (tab === ToolbarTab.BUILD) {
      closeStockpilePanel();
      openConstructionPanel();
    } else if (tab === ToolbarTab.STOCKPILE) {
      closeConstructionPanel();
      openStockpilePanel();
    }
  };

  // Keep activeToolbarTab in sync with panel states
  const isBuildOpen = isConstructionPanelOpen.value;
  const isStockpileOpen = isStockpilePanelOpen.value;

  // Update active tab based on panel states (in case panels are opened/closed externally)
  if (isBuildOpen && activeToolbarTab.value !== ToolbarTab.BUILD) {
    activeToolbarTab.value = ToolbarTab.BUILD;
  } else if (isStockpileOpen && activeToolbarTab.value !== ToolbarTab.STOCKPILE) {
    activeToolbarTab.value = ToolbarTab.STOCKPILE;
  } else if (!isBuildOpen && !isStockpileOpen && activeToolbarTab.value !== null) {
    activeToolbarTab.value = null;
  }

  return (
    <div class={styles.root()}>
      <div class={styles.container()}>
        {/* Left stud */}
        <div class={styles.stud()} />

        {/* Tab buttons */}
        {TABS.map((tab) => {
          const isActive = activeToolbarTab.value === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              class={`${styles.tabButton()} ${isActive ? styles.tabButtonActive() : ''}`}
              onClick={() => handleTabClick({ tab: tab.id })}
              aria-pressed={isActive}
            >
              <div class={styles.tabIcon()}>
                <TabIcon src={tab.icon} tabId={tab.id} />
              </div>
              <span class={styles.tabLabel()}>{t(tab.labelKey)}</span>
            </button>
          );
        })}

        {/* Right stud */}
        <div class={styles.stud()} />
      </div>
    </div>
  );
};
